import type { CategoryRow, NameRow, PositionRow, VendorRow } from '../validators/sheets';

// The Zod-backed validators (~17 KiB gzip) are only needed once the
// `/api/vendors` body lands, so we defer the module to a dynamic import and
// kick it off in parallel with the network round-trip (see `callEdgeApi`).
// Keeping the row types above as `import type` erases them at build time, so
// nothing regresses in the type surface.
type SheetsValidators = typeof import('../validators/sheets');

// ---------------------------------------------------------------------------
// Typed errors
// ---------------------------------------------------------------------------

export type FetchErrorKind =
  | 'rate-limited'
  | 'unauthorized'
  | 'unavailable'
  | 'malformed'
  | 'unknown';

export class SheetsFetchError extends Error {
  readonly kind: FetchErrorKind;
  readonly status?: number | undefined;

  constructor(kind: FetchErrorKind, message: string, status?: number) {
    super(message);
    this.name = 'SheetsFetchError';
    this.kind = kind;
    this.status = status;
  }
}

function classifyHttpStatus(status: number): FetchErrorKind {
  if (status === 429) return 'rate-limited';
  if (status === 401 || status === 403) return 'unauthorized';
  if (status >= 500 && status < 600) return 'unavailable';
  return 'unknown';
}

// ---------------------------------------------------------------------------
// API call
// ---------------------------------------------------------------------------

export interface ApiPayload {
  version: string;
  vendors: VendorRow[];
  positions: PositionRow[];
  categories: CategoryRow[];
  names: NameRow[];
}

export async function callEdgeApi(): Promise<ApiPayload> {
  // Start loading the validators chunk now so it downloads while we wait on
  // the network, not after — the two latencies overlap instead of stacking.
  const validatorsPromise = import('../validators/sheets');
  // Pre-attach a rejection handler so early-throw paths don't leave a floating
  // chunk-load rejection (PostHog captures it as unrelated noise); the await
  // below still surfaces the failure when the validators are actually needed.
  validatorsPromise.catch(() => {});

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12_000);
  let response: Response;
  try {
    response = await fetch('/api/vendors', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Network error contacting /api/vendors';
    throw new SheetsFetchError('unavailable', message);
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // body might not be JSON
    }
    const kind: FetchErrorKind =
      (body &&
      typeof body === 'object' &&
      'kind' in body &&
      typeof (body as { kind: unknown }).kind === 'string'
        ? ((body as { kind: string }).kind as FetchErrorKind)
        : null) ?? classifyHttpStatus(response.status);
    const message =
      (body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : null) ?? `/api/vendors returned ${response.status}`;
    throw new SheetsFetchError(kind, message, response.status);
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new SheetsFetchError('malformed', '/api/vendors returned non-JSON body');
  }

  if (!raw || typeof raw !== 'object') {
    throw new SheetsFetchError('malformed', '/api/vendors body was not an object');
  }
  const obj = raw as Record<string, unknown>;
  if (
    !Array.isArray(obj.vendors) ||
    !Array.isArray(obj.positions) ||
    !Array.isArray(obj.categories)
  ) {
    throw new SheetsFetchError('malformed', '/api/vendors body missing required arrays');
  }
  // Defense in depth — the Edge function refuses to serve an empty Vendors or
  // Positions tab (502 malformed), so an empty array here means a stale-cached
  // empty payload (CDN or SW). Surface the error banner instead of rendering
  // a blank market. Categories/names may legitimately be empty.
  if (obj.vendors.length === 0 || obj.positions.length === 0) {
    throw new SheetsFetchError(
      'malformed',
      '/api/vendors returned no vendors or positions — refusing to render an empty market',
    );
  }

  const validators = await validatorsPromise;

  // Defense in depth — re-validate with the same Zod schemas the server used.
  try {
    return {
      version: typeof obj.version === 'string' ? obj.version : 'v2',
      vendors: validateRowList(validators, obj.vendors as Record<string, unknown>[], 'vendor'),
      positions: validateRowList(
        validators,
        obj.positions as Record<string, unknown>[],
        'position',
      ),
      categories: validateRowList(
        validators,
        obj.categories as Record<string, unknown>[],
        'category',
      ),
      names: Array.isArray(obj.names)
        ? validateRowList(validators, obj.names as Record<string, unknown>[], 'name')
        : [],
    };
  } catch (e) {
    if (e instanceof validators.SheetValidationError) {
      throw new SheetsFetchError('malformed', e.message);
    }
    throw e;
  }
}

// Re-validate a list of typed rows by reconstructing a header+row matrix and
// running the tab parser. The rows arriving from the Edge function already
// passed validation, but we re-check on the client to defend against cache
// poisoning and contract drift.
function validateRowList(
  v: SheetsValidators,
  rows: Record<string, unknown>[],
  kind: 'vendor',
): VendorRow[];
function validateRowList(
  v: SheetsValidators,
  rows: Record<string, unknown>[],
  kind: 'position',
): PositionRow[];
function validateRowList(
  v: SheetsValidators,
  rows: Record<string, unknown>[],
  kind: 'category',
): CategoryRow[];
function validateRowList(
  v: SheetsValidators,
  rows: Record<string, unknown>[],
  kind: 'name',
): NameRow[];
function validateRowList(
  v: SheetsValidators,
  rows: Record<string, unknown>[],
  kind: 'vendor' | 'position' | 'category' | 'name',
): VendorRow[] | PositionRow[] | CategoryRow[] | NameRow[] {
  if (rows.length === 0) return [];
  const headerSet = new Set<string>();
  for (const r of rows) for (const k of Object.keys(r)) headerSet.add(k);
  const headers = Array.from(headerSet);
  const matrix = rows.map((r) => headers.map((h) => (r[h] == null ? '' : String(r[h]))));
  switch (kind) {
    case 'vendor':
      return v.parseVendorsTab(headers, matrix);
    case 'position':
      return v.parsePositionsTab(headers, matrix);
    case 'category':
      return v.parseCategoriesTab(headers, matrix);
    case 'name':
      return v.parseNamesTab(headers, matrix);
  }
}
